import { useEffect } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { invalidateActiveAuctionQueries, reconnectIot } from "@/src/utils/iot";

import { CONNECTION_STATE_CHANGE, ConnectionState } from "@aws-amplify/pubsub";
import { Hub } from "aws-amplify/utils";

// How often the wake detector ticks. While the device sleeps, this timer is
// frozen; on wake it fires late, and the measured gap reveals how long we slept.
const WAKE_CHECK_INTERVAL_MS = 30_000;

// A gap larger than this is treated as a wake-from-sleep (or a long stall). Set
// well above background-tab timer throttling (~1/min) to avoid false positives.
const WAKE_GAP_THRESHOLD_MS = 90_000;

/**
 * Recovers the IoT (Amplify PubSub) WebSocket connection after the device wakes
 * from sleep or the network is restored.
 *
 * AWS Amplify PubSub silently drops its MQTT WebSocket when the device sleeps
 * and does not reliably auto-reconnect. Worse, it reports a stale "Connected"
 * state on wake, so a connection-state check alone is not enough.
 *
 * This hook recovers through three independent triggers so a passive,
 * unfocused observer tab (the one most likely to miss bids) is always covered:
 *  - a wake-from-sleep timer that works regardless of focus/visibility,
 *  - the page becoming visible again,
 *  - the browser coming back online.
 *
 * On a wake/online trigger it force-reconnects (ignoring the stale state) and
 * always invalidates the dealer auction queries so the UI immediately reflects
 * any bids that arrived while disconnected.
 *
 * @param enabled whether recovery listeners should be active (typically when the
 * user is authenticated and the initial subscription has been set up)
 */
const useIotConnectionRecovery = (enabled: boolean) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleRecovery = async (force: boolean) => {
      try {
        const didReconnect = await reconnectIot(force);
        // Always refetch on a forced (wake/online) recovery: the socket may have
        // reported a stale state and silently missed events while disconnected.
        if (didReconnect || force) {
          invalidateActiveAuctionQueries(queryClient);
        }
      } catch (error) {
        console.error("[Baana IOT] connection recovery failed", error);
      }
    };

    // Tracks wall-clock progress. While the device sleeps or the tab is hidden
    // this stops advancing, so the gap on resume reveals how long we were away.
    let lastTick = Date.now();

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const gap = Date.now() - lastTick;
      lastTick = Date.now();
      // After a long hidden/sleep gap, Amplify reports a stale "Connected"
      // state, so a non-forced reconnect would be skipped and the first live
      // message (e.g. a bid placed right after wake) would be lost. Force the
      // reconnect in that case; for normal short tab switches, stay light.
      handleRecovery(gap > WAKE_GAP_THRESHOLD_MS);
    };

    const handleOnline = () => {
      handleRecovery(true);
    };

    const wakeTimer = window.setInterval(() => {
      const now = Date.now();
      const gap = now - lastTick;
      lastTick = now;
      if (gap > WAKE_GAP_THRESHOLD_MS) {
        handleRecovery(true);
      }
    }, WAKE_CHECK_INTERVAL_MS);

    // Trigger recovery when Amplify itself detects a disconnect. The other three
    // triggers cover sleep/network scenarios where Amplify may report a stale
    // "Connected" state and never fires this event; this one covers live drops
    // on an active focused tab where Amplify does detect and report the change.
    const removeHubListener = Hub.listen("pubsub", (data) => {
      const { payload } = data;
      if (payload.event !== CONNECTION_STATE_CHANGE) return;
      const state = (payload.data as { connectionState: ConnectionState })
        .connectionState;
      if (
        state === ConnectionState.Disconnected ||
        state === ConnectionState.ConnectionDisrupted
      ) {
        void handleRecovery(true);
      }
    });

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      removeHubListener();
      window.clearInterval(wakeTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, [enabled, queryClient]);
};

export default useIotConnectionRecovery;
